import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import { DragDropContext } from 'react-beautiful-dnd';
import { Link } from 'react-router-dom';
import { SUBTABS } from './ConstPaths';
import Body from '../Components/layout/Body';
import BodySiteColumn from './BodySiteColumn';

const PHYSICAL_INDEPTH_HELP = `
  <div>
    <p><strong>Purpose:</strong> To help you identify the most promising access sites, the ones you want to try first.</p>
    <p>Your starting point here is the list of body sites that you selected in the previous step, the ones that seemed to be worth looking into more thoroughly. Each body site in the list has its own help content, with specific tips and tricks (and sometimes video) for assessing that particular body site. To view it, just click the little info icon.</p>
    <p>Look for more natural / intuitive methods to start with. For example, if you are looking for yes / no responses, consider working through this hierarchy: head nod / shake, then thumbs up / down, then other movements. This is just a starting point for consideration; of course the choice ultimately depends on what is most functional for the individual.</p>
    <p><strong>Note:</strong> Seating and positioning may be a factor in prioritizing access sites. Solid seating and positioning supports can enhance consistent movement and decrease fatigue.
    <p>With that in mind, be sure the client's positioning system is available and set up properly to maximize stability and correct alignment.  Don't start assessing for access while seated unsupported at edge of a bed or mat, or while seated on the floor. Your client should be comfortable and well-supported.</p>
    <p>A good clue for whether seating / positioning is OK is to observe the client's head.  Can they support it? Is the head flopped over, or down?</p>
    <p>If you suspect the client is struggling due to instability or other seating issues, get some help to problem-solve this. Refer to your seating/positioning specialist, an ATP (assistive technology professional), or interdisciplinary team.</p> 

  </div>
`
const propTypes = {
  firstName: PropTypes.string,
  bodySites: PropTypes.arrayOf(PropTypes.string),
  bodySiteComments: PropTypes.string,
  onChangeBodySiteRankings: PropTypes.func.isRequired,
  onChangeBodySiteComments: PropTypes.func.isRequired,
  saveFormData: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired
};

const defaultProps = {
  firstName: 'the client',
  bodySites: [],
  bodySiteComments: ''
};

//content sub-component with unique content for this page
class PhysicalIndepthContent extends React.Component {
  onDragEnd = (result) => {
    const { destination, source, draggableId } = result;

    //if null destination, do nothing
    if(!destination){
      return;
    }

    //Alex Riordan also likes to see if the location changed at all,
    //and if not, just return
    if (
      //only need this if there is more than one column
      //destination.droppableId === source.droppableId &&
      destination.index === source.index
    ){
      return;
    }

    const newBodySites = Array.from(this.props.bodySites);
    newBodySites.splice(source.index, 1);
    newBodySites.splice(destination.index, 0, draggableId);
    
    this.props.onChangeBodySiteRankings(newBodySites);
  }

  render(){
    const firstName = this.props.firstName;
    const bodySites = this.props.bodySites;
    const bodySiteComments = this.props.bodySiteComments;
    const onChangeBodySiteComments = this.props.onChangeBodySiteComments;

    const rankerColumn = {
      id: 'column-1',
      title: 'Body Sites',
      bodySites: bodySites
    }
    const bs_path = SUBTABS.PHYSICAL;
    const hasBodySites = (Array.isArray(bodySites) && (bodySites.length > 0));
    
    return(
      <>
      { hasBodySites
        ?
        <>
        <div>To do that, <strong>observe movements for each body site</strong>, considering aspects such as:</div>
        <ul>
          <li>how many different movements can be performed</li>
          <li>consistency of repetitions</li>
          <li>fatigue or pain during repetitions</li>
          <li>ease of performing the movement</li>
          <li>range of motion</li>
          <li>level of control</li>
          <li>amount of force generated</li>
        </ul>
        <p>Based on your observations, <strong>drag the most promising access site to the top</strong>, 
          followed by the next-most promising, and so on.
        </p>
        </>
        :
        <p><strong>Note:</strong> You need to <Link to={bs_path}>choose candidate body sites</Link> first. Then you can assess each of those here in more depth.</p>

      }

      <DragDropContext onDragEnd = {this.onDragEnd}>
          <BodySiteColumn column={rankerColumn} bodySites={rankerColumn.bodySites} />
      </DragDropContext>
      <br />
      <Form>
        <FormGroup>
          <Label for="body-site-comments" className="aa-bold-text">
            Notes:
          </Label>
          <Input
            id="body-site-comments"
            value={bodySiteComments}
            onChange={onChangeBodySiteComments}
            name="body-site-comments"
            type="textarea"
          />
        </FormGroup>
      </Form>
      </>
    );
  }
}

//main component for this page
//basic pattern is to set up the pageData, define the unique content for the page (above),
//then render the page using <Body> component
function EvaluateAbilitiesPhysicalIndepth(props) {
  const firstName = props.firstName;
  const bodySites = props.bodySites;
  const bodySiteComments = props.bodySiteComments;
  const onChangeBodySiteRankings = props.onChangeBodySiteRankings;
  const onChangeBodySiteComments = props.onChangeBodySiteComments;
  const saveFormData= props.saveFormData;

  const onContinue = props.onContinue;

  console.log("rendering EvaluateAbilitiesPhysicalIndepth with bodySites: " + bodySites);
  
  const pageData = {
    image: "./assets/images/target-icon.jpg",
    faIcon: "fa-child",
    pageSymbolClassName: "aa-section-symbol eval",
    title: `Now look at the possible access sites in more depth`,
    subTitle: "From the sites you identified, let's try to identify the most promising access sites, the ones you want to try first.",
    learnMore: {linkText: "Learn More about assessing physical access sites",
                title: "Assessing physical access sites",
                content: PHYSICAL_INDEPTH_HELP
                },
    nextRoute: "", //not used since we use onContinue
    sidebar: false
  }

  useEffect(() => {
    console.log('EAPIndepth mounted');
    return () => {
      console.log('EAPIndepth unmounting and saving...');
      saveFormData();
    }
  }, [saveFormData])

  return(
    <>
    <Body page={pageData} 
      contentComponent={
        <PhysicalIndepthContent 
          firstName={firstName}
          bodySites={bodySites}
          bodySiteComments={bodySiteComments}
          onChangeBodySiteRankings={onChangeBodySiteRankings}
          onChangeBodySiteComments={onChangeBodySiteComments}
        />
      }
      onContinue={onContinue} 
    />
    </>
  );
}

EvaluateAbilitiesPhysicalIndepth.propTypes = propTypes;
EvaluateAbilitiesPhysicalIndepth.defaultProps = defaultProps;

export default EvaluateAbilitiesPhysicalIndepth;